import {
    faFileInvoiceDollar,
    faFileLines,
    faFileZipper,
    faSearch,
    faShareFromSquare,
    faStar,
    faTrash,
    faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../../../../AlertProvider';
import { useAuth } from '../../../../../AuthProvider';
import Button from '../../../../../components/Button';
import ButtonVariant from '../../../../../components/ButtonVariant';
import Modal from '../../../../../components/Modal';
import { alertType } from '../../../../../constants/alertTypeConstant';
import { clientsPage } from '../../../../../constants/pagesRoutesContants';
import {
    requestStatus,
    successStatus,
} from '../../../../../constants/requestStatusContants';
import Action from '../../../../../enums/ActionsEnum';
import Role from '../../../../../enums/RoleEnum';
import LawyerToCaseInterface from '../../../../../interfaces/LawyerToCaseInterface';
import NavDataInterface from '../../../../../interfaces/NavDataInterface';
import { postCaseBookmark } from '../../../../../services/caseService';
import {
    addLawyerToCase,
    rejectCase,
    transferCaseToLawyer,
} from '../../../../../services/lawyerCaseService';
import CaseType from '../../../../../types/CaseType';
import LawyerType from '../../../../../types/LawyerType';
import SearchMyLawyer from '../SearchMyLawyer';
import styles from './style.module.scss';

interface Props {
    userCase: CaseType;
}

function CaseActions(props: Props) {
    const { userCase } = props;
    const { user } = useAuth();
    const alert = useAlert();
    const navigate = useNavigate();
    const [addLawyerModalOpened, setAddLawyerModalOpened] = useState(false);
    const [lawyerValue, setLawyerValue] = useState<LawyerType | null>(null);

    const { mutate: addLawyer } = useMutation({
        mutationFn: async (lawyerToCaseData: LawyerToCaseInterface) => {
            if (lawyerToCaseData) {
                const { data, status } =
                    await addLawyerToCase(lawyerToCaseData);

                if (successStatus.includes(status)) {
                    alert.show({
                        children: 'Votre demande a été envoyée avec succès',
                        type: alertType[status],
                    });
                } else {
                    alert.show({
                        children:
                            'Erreur! Une erreur est survenue. Veuillez réessayer plus tard.',
                        type: alertType[status],
                    });
                }
                return data;
            }
        },
    });

    const { mutate: transferLawyer } = useMutation({
        mutationFn: async (lawyerToCaseData: LawyerToCaseInterface) => {
            if (lawyerToCaseData) {
                const { data, status } =
                    await transferCaseToLawyer(lawyerToCaseData);

                if (successStatus.includes(status)) {
                    alert.show({
                        children: 'Votre demande a été envoyée avec succès',
                        type: alertType[status],
                        onClose: () => {
                            navigate(clientsPage.path);
                        },
                    });
                } else {
                    alert.show({
                        children:
                            'Erreur! Une erreur est survenue. Veuillez réessayer plus tard.',
                        type: alertType[status],
                    });
                }
                return data;
            }
        },
    });

    const [action, setAction] = useState<Action.add | Action.edit | null>();
    const actions = {
        [Action.add]: { request: addLawyer },
        [Action.edit]: { request: transferLawyer },
    };

    const { mutate: addCaseBookmark } = useMutation({
        mutationFn: async (id: string) => {
            const result = await postCaseBookmark(id);
            return result;
        },
    });

    const { mutate: reject } = useMutation({
        mutationFn: async (id: string) => {
            const result = await rejectCase(id);
            if (result?.status === requestStatus.OK) {
                navigate(clientsPage.path);
            }
            return result;
        },
    });

    const handleActionLawyerClick = () => {
        if (userCase.id && lawyerValue?.id && action) {
            actions[action].request({
                clientCaseId: userCase.id,
                lawyerId: lawyerValue?.id,
            });
        }
    };

    const handleClickNewDoc = () => {
        // setTypeAction('new_file');
    };

    const handleSearch = () => {
        // setTypeAction('view_search');
    };
    const handleClickAddLawyer = () => {
        setAddLawyerModalOpened(true);
        setAction(Action.add);
    };

    const handleClickTransferToLawyer = () => {
        setAddLawyerModalOpened(true);
        setAction(Action.edit);
    };

    const handleClickRejectCase = () => {
        reject(userCase.id);
    };

    const handleClickAddCaseBookmark = () => {
        addCaseBookmark(userCase.id);
    };
    const defaultToolbar: NavDataInterface[] = [
        {
            id: 'caseToolbar2',
            icon: faUserPlus,
            label: 'Ajouter un avocat',
            onClick: handleClickAddLawyer,
        },
    ];

    const lawyerToolbar: NavDataInterface[] = [
        {
            id: 'caseToolbar1',
            iconType: 'regular',
            icon: faFileLines,
            label: 'Nouveau document',
            onClick: handleClickNewDoc,
        },

        {
            id: 'caseToolbar3',
            icon: faFileZipper,
            label: 'Exporter dossier',
        },
        {
            id: 'caseToolbar4',
            icon: !userCase.isBookmarked ? faStar : faStar,
            label:
                (!userCase.isBookmarked ? 'Ajouter aux' : 'Retirer des') +
                ' favoris',
            onClick: handleClickAddCaseBookmark,
        },
        {
            id: 'caseToolbar5',
            icon: faSearch,
            label: 'Articles ou Jurisprudences',
            onClick: handleSearch,
        },
        {
            id: 'caseToolbar6',
            icon: faFileInvoiceDollar,
            label: 'Nouvelle facture',
            onClick: handleSearch,
        },
        {
            id: 'caseToolbar7',
            icon: faShareFromSquare,
            label: 'Transférer dossier',
            onClick: handleClickTransferToLawyer,
        },
        {
            id: 'caseToolbar8',
            icon: faTrash,
            label: 'Refuser dossier',
            onClick: handleClickRejectCase,
        },
    ];

    let actionBarData = defaultToolbar;
    if (user?.role === Role.Lawyer) {
        actionBarData = [...defaultToolbar, ...lawyerToolbar];
    }

    return (
        <div className={styles.infoContainer}>
            <Modal
                opened={addLawyerModalOpened}
                setOpened={setAddLawyerModalOpened}
                title={'Ajouter un Avocat'}
            >
                <SearchMyLawyer onOptionSelected={setLawyerValue} />
                <Button onClick={handleActionLawyerClick}>Ajouter</Button>
            </Modal>
            <div className={styles.actionBtnContainer}>
                {actionBarData.map((btn: NavDataInterface) => (
                    <ButtonVariant
                        variant="secondaryLight"
                        onClick={btn.onClick}
                    >
                        <div className={styles.navBarBtn}>
                            {btn.icon && (
                                <FontAwesomeIcon
                                    icon={btn.icon}
                                    className={styles.btnIcon}
                                />
                            )}
                            <label className={styles.btnLabel}>
                                {btn.label}
                            </label>
                        </div>
                    </ButtonVariant>
                ))}
            </div>
        </div>
    );
}

export default CaseActions;
